/**
 * Chapter 10, the content: the pillars (16), one pillar with its series (17),
 * the mix and the volume (19), the brand codes (50).
 */
import type { ContentClass, StrategyDocument } from "@/types/strategyDocument.type";
import { chapterSpec } from "../deck/deckModel";
import {
  Eyebrow, Foot, Headline, Picture, Slide, SourceLine, V,
  countWord, dayShort, figure, joinAnd, pillarColour, platformName, slideText,
  type TemplateContext, NO_FIGURE
} from "./kit";

/** Posts a month per pillar: the monthly total split by share, largest remainder first, so they add up. */
export function pillarPosts(doc: StrategyDocument): Record<string, number> {
  const pillars = doc.content?.pillars ?? [];
  const total = doc.content?.volume?.monthly_total;
  if (!total) return {};
  const raw = pillars.map((p) => ({ id: p.id, exact: (p.share_pct / 100) * total }));
  const out: Record<string, number> = Object.fromEntries(raw.map((r) => [r.id, Math.floor(r.exact)]));
  let left = total - Object.values(out).reduce((a, b) => a + b, 0);
  [...raw].sort((a, b) => (b.exact % 1) - (a.exact % 1)).forEach((r) => {
    if (left > 0 && r.exact % 1 > 0) { out[r.id] += 1; left -= 1; }
  });
  return out;
}

const CLASS_COLOURS: Record<ContentClass, string> = { Culture: "#207cf5", Product: "#666666", Brand: "#121212", Promotion: "#f66666" };
const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/* ── 16 The pillars ───────────────────────────────────────────────────── */

export function PillarsSlide({ ctx }: { ctx: TemplateContext }) {
  const { doc } = ctx;
  const content = doc.content!;
  const t = slideText(ctx);
  const posts = pillarPosts(doc);
  const total = content.volume?.monthly_total;
  const situations = doc.audience?.situations ?? [];
  const goals = doc.goals?.list ?? [];
  const category = doc.competitors?.mix_summary?.classes ?? [];
  const used = [...new Set(content.pillars.map((p) => p.class).filter((c): c is ContentClass => !!c))];
  const ours = (c: ContentClass) => content.pillars.filter((p) => p.class === c).reduce((a, p) => a + p.share_pct, 0);
  const against = category.filter((c) => used.includes(c.class)).sort((a, b) => b.share_pct - a.share_pct);
  const first = used.filter((c) => ours(c) > (category.find((x) => x.class === c)?.share_pct ?? 100));
  const n = content.pillars.length;
  const fallback = `${capital(countWord(n))} pillar${n === 1 ? "" : "s"},${total ? ` *${total} posts a month.*` : ""}`;
  return (
    <Slide>
      <div className="sv-col sv-fill">
        <Eyebrow ctx={ctx} />
        <Headline text={t.headline} fallback={fallback} size={54} lineHeight={60} />
        <div className="sv-pillars">
          {content.pillars.slice(0, 3).map((p) => {
            const goal = goals.find((g) => g.id === p.goal_id);
            const serves = [
              p.situation_ids.map((id) => situations.find((s) => s.id === id)).filter(Boolean).map((s) => s!.short ?? s!.situation).join(", "),
              goal ? `goal: ${goal.short ?? goal.name}${goal.id === doc.goals?.watch_most ? ` ${figure(goal.target.value, goal.target.unit, { lower: true })}` : ""}` : "",
            ].filter(Boolean).join(" · ");
            return (
              <div key={p.id} className="sv-pillar">
                <Picture ctx={ctx} slot={`pillar:${p.id}`} label={`${p.name}’s picture`} className="sv-pillar-pic" />
                <p className="sv-pillar-name"><i style={{ background: pillarColour(doc, p.id) }} /><V>{p.name}</V></p>
                <p className="sv-pillar-class">
                  {p.class ? <V className="sv-pillar-pill" style={{ background: CLASS_COLOURS[p.class] }}>{p.class}</V> : null}
                  {serves ? <V>{serves}</V> : null}
                </p>
                <V as="p" className="sv-pillar-definition">{p.definition}</V>
                <span className="sv-push" />
                <div className="sv-pillar-share">
                  <V as="b">{p.share_pct}%</V>
                  <p>of the mix{posts[p.id] != null ? <><br /><V>{posts[p.id]}</V> posts a month</> : null}</p>
                </div>
              </div>
            );
          })}
        </div>
        <Foot ctx={ctx} extra={against.length ? (
          <> · Against the category: <V>{against.map((c) => `${c.class} ${c.share_pct}%`).join(", ")}</V>.{first.length ? <> We put <V>{joinAnd(first)}</V> first.</> : null}</>
        ) : null} />
      </div>
    </Slide>
  );
}

/* ── 17 A pillar ──────────────────────────────────────────────────────── */

export function PillarSlide({ ctx }: { ctx: TemplateContext }) {
  const { doc } = ctx;
  const content = doc.content!;
  const i = ctx.slide.template.id === "pillar" ? ctx.slide.template.pillar : 0;
  const p = content.pillars[i];
  const t = slideText(ctx);
  const posts = pillarPosts(doc)[p.id];
  const series = content.series.filter((s) => s.pillar_id === p.id);
  const words = content.search_words.filter((w) => w.pillar_id === p.id).flatMap((w) => w.words);
  const mix = [`${p.share_pct}% of the mix`, posts != null ? `${posts} posts a month` : null].filter(Boolean).join(", ");
  return (
    <Slide className="sv-pillar-detail">
      <div className="sv-col sv-pillar-copy">
        <Headline text={t.headline} fallback={p.name} size={70} lineHeight={74} valueWords />
        <p className="sv-sub sv-pillar-sub"><V>{p.definition}</V> <V>{capital(mix)}</V>{p.class ? <>, under <V>{p.class}</V></> : null}.</p>
        <div className="sv-series">
          {series.slice(0, 5).map((s) => (
            <div key={s.id} className="sv-series-row">
              <V as="p" className="sv-series-name">{s.name}</V>
              <p className="sv-series-line">{s.line ? <V>{s.line}</V> : null}</p>
              <p className="sv-series-when"><V>{[s.when, s.where ?? joinAnd(s.platforms.map(platformName))].filter(Boolean).join(" · ")}</V></p>
            </div>
          ))}
        </div>
        <span className="sv-push" />
        {words.length ? (
          <p className="sv-pillar-words">
            <span className="sv-key">Search words, from chapter {chapterSpec("industry")?.number}</span>
            <V dir="auto">{words.join(" · ")}</V>
          </p>
        ) : null}
        <div className="sv-foot">
          <span className="sv-pillar-foot"><i style={{ background: pillarColour(doc, p.id) }} /><span>Pillar {i + 1} of {content.pillars.length} · <V>{mix.replace(", ", " · ")}</V></span></span>
          <span className="sv-push" />
          <b>{ctx.page}</b>
        </div>
      </div>
      <Picture ctx={ctx} slot={`pillar:${p.id}`} label={`${p.name}’s picture`} className="sv-pillar-detail-pic" />
    </Slide>
  );
}

/* ── 19 The mix and the volume ────────────────────────────────────────── */

const VERTICAL_NAMES: Partial<Record<string, string>> = { INSTAGRAM: "Reels", TIKTOK: "TikToks", YOUTUBE: "Shorts" };

export function MixSlide({ ctx }: { ctx: TemplateContext }) {
  const { doc } = ctx;
  const content = doc.content!;
  const volume = content.volume!;
  const t = slideText(ctx);
  const total = volume.monthly_total;
  const posts = pillarPosts(doc);
  const cal = volume.calendar_mix;
  const platforms = (doc.platforms?.list ?? []).map((p) => p.platform);
  const pct = (n: number) => `${Math.round((n / Math.max(1, total)) * 100)}%`;
  const formats = cal ? [
    { n: cal.vertical_video_posts, label: "Vertical video", note: platforms.map((p) => VERTICAL_NAMES[p]).filter(Boolean).join(", ") },
    { n: cal.static_posts, label: "Static", note: "carousels and pictures" },
    { n: cal.horizontal_video_posts, label: "Horizontal video", note: platforms.includes("YOUTUBE") ? "YouTube long video" : "long video" },
  ] : [];
  const weekly = Math.round((total * 12) / 52);
  const mix = content.mix;
  return (
    <Slide>
      <div className="sv-col sv-fill">
        <Eyebrow ctx={ctx} />
        <Headline text={t.headline} fallback="What we make, *month after month.*" size={54} lineHeight={60} />
        <div className="sv-mixbody">
          <div className="sv-mix-total">
            <V as="b">{total}</V>
            <p className="sv-mix-total-label">posts a month, made once</p>
            {volume.per_platform.length ? (
              <p className="sv-mix-total-weekly">
                <V>{weekly}</V> a week: {volume.per_platform.map((v, j) => <span key={v.platform}>{j ? ", " : null}<V>{v.posts_per_month}</V>{j ? " to " : " go to "}{platformName(v.platform)}</span>)}. One post can go to several platforms.
              </p>
            ) : null}
          </div>
          <div className="sv-mix-split">
            {formats.length ? (
              <div className="sv-mix-formats">
                <p className="sv-mix-head">By format</p>
                <div className="sv-mix-format-row">
                  {formats.map((f) => (
                    <div key={f.label} className="sv-mix-format">
                      <V as="b">{f.n}</V>
                      <p>{f.label}</p>
                      <p>{f.note ? <>{f.note} · </> : null}<V>{pct(f.n)}</V></p>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            <div className="sv-mix-pillars">
              <p className="sv-mix-head">By pillar</p>
              {content.pillars.map((p) => (
                <div key={p.id} className="sv-mix-pillar">
                  <i style={{ background: pillarColour(doc, p.id) }} />
                  <V as="p">{p.name}</V>
                  <span className="sv-mix-track"><span style={{ width: `${((posts[p.id] ?? 0) / Math.max(1, total)) * 100}%` }} /></span>
                  <V as="b">{posts[p.id] ?? NO_FIGURE}</V>
                </div>
              ))}
            </div>
          </div>
        </div>
        <span className="sv-push" />
        {mix ? (
          <div className="sv-kinds">
            <div className="sv-kinds-row">
              <p className="sv-key">Three kinds of post</p>
              <div className="sv-kinds-bar">
                <span style={{ flexGrow: mix.regular_series_pct }}>Regular series <V>{mix.regular_series_pct}%</V></span>
                <span data-tone="blue" style={{ flexGrow: mix.big_moments_pct }}>Big moments <V>{mix.big_moments_pct}%</V></span>
                <span data-tone="lime" style={{ flexGrow: mix.reactions_pct }}>Reactions <V>{mix.reactions_pct}%</V></span>
              </div>
            </div>
            <div className="sv-kinds-row">
              <p className="sv-key">Proven against new</p>
              <div className="sv-kinds-bar">
                <span style={{ flexGrow: mix.proven_pct }}>Proven formats <V>{mix.proven_pct}%</V></span>
                <span data-tone="grey" style={{ flexGrow: mix.new_pct }}>New <V>{mix.new_pct}%</V></span>
              </div>
            </div>
          </div>
        ) : null}
        <Foot ctx={ctx} label="The content system" flag={volume.source?.label ? <SourceLine className="sv-flag-line">{volume.source.label}</SourceLine> : null} />
      </div>
    </Slide>
  );
}

/* ── 50 Brand codes ───────────────────────────────────────────────────── */

const CODE_CHIPS = [
  { bg: "var(--blue)", fg: "#ffffff" },
  { bg: "var(--lime)", fg: "#121212" },
  { bg: "#04e735", fg: "#121212" },
  { bg: "var(--red)", fg: "#ffffff" },
];

export function CodesSlide({ ctx }: { ctx: TemplateContext }) {
  const codes = ctx.doc.content!.brand_codes;
  const t = slideText(ctx);
  return (
    <Slide>
      <div className="sv-col sv-fill">
        <Eyebrow ctx={ctx} />
        <Headline text={t.headline} fallback="Ours in the *first two seconds.*" />
        <div className="sv-codes">
          {codes.slice(0, 4).map((c, i) => {
            const chip = CODE_CHIPS[i % CODE_CHIPS.length];
            const seen = [c.evidence?.series, c.evidence?.date ? dayShort(c.evidence.date) : null].filter(Boolean).join(" · ");
            return (
              <div key={c.id} className="sv-code">
                <div className="sv-code-frame">
                  <Picture ctx={ctx} slot={`code:${c.id}`} label={`A post that shows ${c.code}`} url={c.evidence?.picture_url} className="sv-fill-pic" />
                  <p className="sv-code-chip" style={{ background: chip.bg, color: chip.fg }}><b>{String(i + 1).padStart(2, "0")}</b><V>{c.code}</V></p>
                  {seen ? <p className="sv-code-seen">Seen in <V>{seen}</V></p> : null}
                </div>
                <V as="p" className="sv-code-name">{c.code}</V>
                <V as="p" className="sv-code-rule">{c.rule}</V>
              </div>
            );
          })}
        </div>
        <Foot ctx={ctx} extra=" · Each code with a post that shows it" />
      </div>
    </Slide>
  );
}

